import { useCallback, useEffect, useMemo, useRef, useState } from "react"

import { useAgentMutationsAll } from "@/agents/agent-mutations"
import { useAgentsList } from "@/agents/catalog"
import { ENTITY_ICONS } from "@/constants/entity-icons"
import { Checkbox, SearchInput, Skeleton } from "@coval/ui"
import { ChevronRight, Dna } from "lucide-react"

import { FilterPopover } from "@/components/filters/FilterPopover"
import { isFilterSectionDisabled } from "@/components/filters/filterSectionVisibility"
import { FilterTrigger } from "@/components/filters/FilterTrigger"
import { cn } from "@/utils/cn"

import { useRunsFilter } from "../../contexts/RunsFilterContext"

const AgentIcon = ENTITY_ICONS.agents

interface AgentFilterSectionProps {
  isOpen: boolean
  onToggle: () => void
  showMutations?: boolean
  /** When true, filter is locked (e.g. by URL agent_id) and greyed out */
  disabled?: boolean
  /** When false, defers data-fetching until the tier-1 table query resolves */
  filterEnabled?: boolean
}

export const AgentFilterSection: React.FC<AgentFilterSectionProps> = ({
  isOpen,
  onToggle,
  showMutations = false,
  disabled = false,
  filterEnabled,
}) => {
  const {
    agentFilters: rawAgentFilters,
    addAgentFilter,
    removeAgentFilter,
    mutationFilters: rawMutationFilters,
    addMutationFilter,
    removeMutationFilter,
  } = useRunsFilter()

  // Defensive: ensure filters are always arrays (handles corrupted localStorage)
  const agentFilters = Array.isArray(rawAgentFilters) ? rawAgentFilters : []
  const mutationFilters = Array.isArray(rawMutationFilters)
    ? rawMutationFilters
    : []
  const isDeferred = filterEnabled === false
  const {
    data: agents,
    isError: isAgentsError,
    isLoading: agentsLoading,
  } = useAgentsList({
    enabled: filterEnabled !== false,
  })
  const [searchQuery, setSearchQuery] = useState("")
  const searchInputRef = useRef<HTMLInputElement>(null)
  const [expandedAgents, setExpandedAgents] = useState<Set<string>>(new Set())

  // Fetch all mutations in a single call (avoids N separate API calls)
  const { data: allMutations } = useAgentMutationsAll({
    enabled: showMutations && filterEnabled !== false,
  })

  // Build mutations by agent map
  const mutationsByAgent = useMemo(() => {
    if (!showMutations || !allMutations) {
      return {}
    }
    const byAgent: Record<
      string,
      Array<{ id: string; display_name: string }>
    > = {}

    allMutations.forEach((mutation) => {
      if (mutation.status === "ACTIVE") {
        if (!byAgent[mutation.agent_id]) {
          byAgent[mutation.agent_id] = []
        }
        byAgent[mutation.agent_id].push({
          id: mutation.id,
          display_name: mutation.display_name,
        })
      }
    })

    return byAgent
  }, [allMutations, showMutations])

  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      const timeoutId = window.setTimeout(
        () => searchInputRef.current?.focus(),
        100,
      )
      return () => window.clearTimeout(timeoutId)
    }
  }, [isOpen])

  useEffect(() => {
    if (!isOpen) {
      setSearchQuery("")
    }
  }, [isOpen])

  // Filter agents and mutations by search query
  const { filteredAgents, matchingMutationsByAgent } = useMemo(() => {
    if (!agents) {
      return { filteredAgents: [], matchingMutationsByAgent: {} }
    }

    const query = searchQuery.toLowerCase().trim()
    if (!query) {
      return {
        filteredAgents: agents,
        matchingMutationsByAgent: {} as Record<string, string[]>,
      }
    }

    const matchingMuts: Record<string, string[]> = {}
    const agentMatches = new Set<string>()

    // Check which agents match directly
    agents.forEach((agent) => {
      const nameMatch =
        (agent.display_name || "").toLowerCase().includes(query) ||
        agent.id.toLowerCase().includes(query)
      if (nameMatch) {
        agentMatches.add(agent.id)
      }
    })

    // Check which mutations match and track their agents
    Object.entries(mutationsByAgent).forEach(([agentId, mutations]) => {
      const matchingMutationIds = mutations
        .filter((m) => m.display_name.toLowerCase().includes(query))
        .map((m) => m.id)

      if (matchingMutationIds.length > 0) {
        matchingMuts[agentId] = matchingMutationIds
        agentMatches.add(agentId)
      }
    })

    const filtered = agents.filter((agent) => agentMatches.has(agent.id))
    return { filteredAgents: filtered, matchingMutationsByAgent: matchingMuts }
  }, [agents, searchQuery, mutationsByAgent])

  const matchingAgentIds = useMemo(
    () => Object.keys(matchingMutationsByAgent),
    [matchingMutationsByAgent],
  )

  useEffect(() => {
    if (matchingAgentIds.length > 0) {
      setExpandedAgents(new Set(matchingAgentIds))
    }
  }, [matchingAgentIds])

  const handleToggleExpand = (agentId: string) => {
    setExpandedAgents((prev) => {
      const newSet = new Set(prev)
      if (newSet.has(agentId)) {
        newSet.delete(agentId)
      } else {
        newSet.add(agentId)
      }
      return newSet
    })
  }

  const handleAgentCheckboxChange = useCallback(
    (agentId: string, checked: boolean) => {
      if (checked) {
        addAgentFilter(agentId)
      } else {
        removeAgentFilter(agentId)
      }
    },
    [addAgentFilter, removeAgentFilter],
  )

  const handleMutationCheckboxChange = useCallback(
    (mutationId: string, checked: boolean) => {
      if (checked) {
        addMutationFilter(mutationId)
      } else {
        removeMutationFilter(mutationId)
      }
    },
    [addMutationFilter, removeMutationFilter],
  )

  const hasActiveFilters = agentFilters.length > 0 || mutationFilters.length > 0

  // Only reserve the expand-chevron gutter when some visible agent can expand,
  // so a list of plain agents lines up with the search field above it.
  const hasExpandableAgent = useMemo(
    () =>
      showMutations &&
      filteredAgents.some(
        (agent) => (mutationsByAgent[agent.id] || []).length > 0,
      ),
    [filteredAgents, mutationsByAgent, showMutations],
  )

  const isUnavailable = isFilterSectionDisabled({
    activeFilterCount: agentFilters.length + mutationFilters.length,
    isDeferred,
    isError: isAgentsError,
    isLoading: agentsLoading,
    optionCount: agents?.length ?? 0,
  })
  const isDisabled = disabled || isUnavailable

  return (
    <FilterPopover
      isOpen={isOpen}
      onToggle={onToggle}
      disabled={isDisabled}
      emptyMessage="No agents available in this workspace."
      trigger={
        <FilterTrigger
          label="Agents"
          icon={<AgentIcon className="w-4 h-4" />}
          isActive={hasActiveFilters}
          disabled={isDisabled}
        />
      }
    >
      <div className="w-full">
        <div className="p-3 border-b border-coval-border">
          <SearchInput
            ref={searchInputRef}
            placeholder="Search agents and mutations..."
            value={searchQuery}
            onChange={(event) => setSearchQuery(event.target.value)}
            onClear={() => setSearchQuery("")}
            aria-label="Search agents and mutations"
          />
        </div>

        <div className="max-h-[50vh] overflow-y-auto">
          {isDeferred || agentsLoading ? (
            <div className="flex justify-center py-4">
              <Skeleton className="h-4 w-16 rounded-md" />
            </div>
          ) : (
            <div className="p-1.5">
              <div className="flex flex-col">
                {filteredAgents.map((agent) => {
                  const agentMutations = mutationsByAgent[agent.id] || []
                  const hasMutations =
                    showMutations && agentMutations.length > 0
                  const isExpanded = expandedAgents.has(agent.id)
                  const isAgentChecked = agentFilters.includes(agent.id)

                  return (
                    <div key={agent.id}>
                      <div className="flex items-stretch rounded-sm hover:bg-surface-contrast">
                        {hasMutations && (
                          <button
                            type="button"
                            onClick={() => handleToggleExpand(agent.id)}
                            className="group flex shrink-0 items-center pl-2 pr-1"
                            aria-label={
                              isExpanded
                                ? `Collapse ${agent.display_name || agent.id} mutations`
                                : `Expand ${agent.display_name || agent.id} mutations`
                            }
                          >
                            <span className="rounded p-0.5 group-hover:bg-surface-1">
                              <ChevronRight
                                className={cn(
                                  "w-3.5 h-3.5 text-coval-text-muted transition-transform",
                                  isExpanded && "rotate-90",
                                )}
                              />
                            </span>
                          </button>
                        )}
                        <label
                          className={cn(
                            "flex min-w-0 flex-1 cursor-pointer items-center gap-2 py-1.5 pr-2",
                            !hasMutations &&
                              (hasExpandableAgent ? "pl-1" : "pl-2"),
                          )}
                        >
                          {!hasMutations && hasExpandableAgent && (
                            <span className="w-[18px] shrink-0" />
                          )}
                          <Checkbox
                            id={`agent-${agent.id}`}
                            checked={isAgentChecked}
                            onCheckedChange={(checked) =>
                              handleAgentCheckboxChange(
                                agent.id,
                                checked === true,
                              )
                            }
                            aria-label={agent.display_name || agent.id}
                          />
                          <span className="flex min-w-0 flex-1 flex-wrap items-center gap-x-2">
                            <span className="text-sm min-w-0 whitespace-normal break-words">
                              {agent.display_name || agent.id}
                            </span>
                            {hasMutations && (
                              <span className="text-xs text-coval-text-muted">
                                {agentMutations.length}{" "}
                                {agentMutations.length === 1
                                  ? "mutation"
                                  : "mutations"}
                              </span>
                            )}
                          </span>
                        </label>
                      </div>

                      {hasMutations && isExpanded && (
                        <div className="ml-6 mt-1 mb-2 pl-2 border-l border-coval-border-active">
                          <p className="mb-1 text-xs text-coval-text-muted">
                            Mutations
                          </p>
                          <div className="flex flex-col">
                            {agentMutations.map((mutation) => {
                              const isMutationChecked =
                                mutationFilters.includes(mutation.id)
                              return (
                                <label
                                  key={mutation.id}
                                  className="flex max-w-full cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 hover:bg-surface-contrast"
                                >
                                  <Checkbox
                                    id={`mutation-${mutation.id}`}
                                    checked={isMutationChecked}
                                    onCheckedChange={(checked) =>
                                      handleMutationCheckboxChange(
                                        mutation.id,
                                        checked === true,
                                      )
                                    }
                                    aria-label={mutation.display_name}
                                  />
                                  <span className="flex min-w-0 flex-1 items-center gap-1.5">
                                    <Dna className="w-3 h-3 text-coval-text-muted shrink-0" />
                                    <span className="text-sm min-w-0 whitespace-normal break-words">
                                      {mutation.display_name}
                                    </span>
                                  </span>
                                </label>
                              )
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  )
                })}
                {filteredAgents.length === 0 && searchQuery && (
                  <div className="py-2 text-center text-sm text-coval-text-muted">
                    No agents or mutations found
                  </div>
                )}
                {filteredAgents.length === 0 && !searchQuery && (
                  <div className="py-2 text-center text-sm text-coval-text-muted">
                    No agents available
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </FilterPopover>
  )
}
